import React, { useMemo, useState } from 'react';
import type { KrKeyword, KrMeasuredFrom, KrStat, KrSummaryItem, KrTone } from '../../growth-keywords/types';
import {
  KR_KEYWORDS,
  KR_MEASURED_FROM,
  KR_STATS,
  KR_SUMMARY,
} from '../../growth-keywords/mockData';
import { GROWTH_KEYWORDS_META, KR_CATEGORIES } from '../../growth-keywords/config';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Pagination } from '../Pagination/Pagination';
import { Segmented } from '../Segmented/Segmented';
import './growth-keywords.css';

const TONE: Record<KrTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function rankTone(rank: string): KrTone {
  if (rank === 'Not on page 1') return 'neutral';
  const n = Number(rank);
  if (n <= 3) return 'success';
  if (n <= 6) return 'gold';
  return 'terra';
}

function deltaParts(delta: number): { label: string; tone: KrTone } {
  if (delta > 0) return { label: `↑ ${String(delta)}`, tone: 'success' };
  if (delta < 0) return { label: `↓ ${String(Math.abs(delta))}`, tone: 'terra' };
  return { label: '—', tone: 'neutral' };
}

function KrCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function KrStatTile({
  label,
  value,
  suffix,
  sub,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  sub: string;
  tone: KrTone;
}) {
  const accent = tone === 'neutral' ? 'var(--ink-0)' : TONE[tone].accent;
  return (
    <KrCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{label}</div>
      <div
        className="mt-2 font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
        style={{ color: accent }}
      >
        {value}
        {suffix ? <span className="text-[var(--ink-4)]">{suffix}</span> : null}
      </div>
      <div className="mt-2 text-[12.5px]" style={{ color: tone === 'neutral' ? 'var(--ink-3)' : accent }}>
        {sub}
      </div>
    </KrCard>
  );
}

function KrSerpResult({
  result,
}: {
  result: NonNullable<KrKeyword['detail']>['serp'][number];
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-md)] p-4',
        result.you
          ? 'border border-[color-mix(in_srgb,var(--success)_45%,transparent)] bg-[var(--success-tint)]'
          : 'bg-[var(--surface-mute)]',
      ].join(' ')}
    >
      <div className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
        <span className="font-[var(--font-mono)]">#{result.position}</span>
        <span className="truncate">{result.url}</span>
        {result.you ? (
          <span className="rounded-[var(--r-pill)] bg-[var(--success)] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-[var(--cream-1)]">
            You
          </span>
        ) : null}
      </div>
      <div className="mt-2 text-[14.5px] font-semibold text-[var(--info)]">{result.title}</div>
      {result.snippet ? (
        <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[var(--ink-3)]">{result.snippet}</p>
      ) : null}
    </div>
  );
}

function KrAdviceList({
  icon,
  tone,
  title,
  items,
  ordered = false,
}: {
  icon: 'check-circ' | 'alert' | 'sparkle';
  tone: KrTone;
  title: string;
  items: string[];
  ordered?: boolean;
}) {
  const t = TONE[tone];
  return (
    <div>
      <div
        className="inline-flex items-center gap-2 text-[13px] font-semibold"
        style={{ color: t.accent }}
      >
        <Icon kind={icon} size={15} stroke={1.9} />
        {title}
      </div>
      <div className="mt-2 space-y-2">
        {items.map((item, index) => (
          <p
            key={item}
            className="rounded-[var(--r-sm)] border px-3 py-2 text-[12.5px] leading-[1.5] text-[var(--ink-2)]"
            style={{
              borderColor: `color-mix(in srgb, ${t.accent} 30%, transparent)`,
              background: t.tint,
            }}
          >
            {ordered ? `${String(index + 1)}. ` : ''}
            {item}
          </p>
        ))}
      </div>
    </div>
  );
}

function KrKeywordRow({
  row,
  open,
  onToggle,
  detailLoading = false,
  briefGenerating = false,
  onRegenerateBrief,
}: {
  row: KrKeyword;
  open: boolean;
  onToggle: () => void;
  detailLoading?: boolean;
  briefGenerating?: boolean;
  onRegenerateBrief?: () => void;
}) {
  const rt = TONE[rankTone(row.rank)];
  const delta = deltaParts(row.delta);

  return (
    <KrCard
      className={[
        'mb-3 overflow-hidden',
        open ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)]' : '',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent px-5 py-4 text-left"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <span
              className="inline-flex items-center rounded-[10px] px-3 py-1 text-[15px] font-bold"
              style={{ background: rt.tint, color: rt.accent }}
            >
              {row.rank}
            </span>
            <span className="truncate text-[15px] font-semibold text-[var(--ink-0)]">
              &ldquo;{row.keyword}&rdquo;
            </span>
            <span className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[12px] font-medium text-[var(--ink-3)]">
              {row.category}
            </span>
            <span className="text-[13px] font-semibold" style={{ color: TONE[delta.tone].accent }}>
              {delta.label}
            </span>
          </div>
          <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
            {row.volume}
            {row.volume !== '—' ? ' searches/mo' : ''} • Leader: {row.leader}
          </p>
        </div>
        <Icon
          kind="down"
          size={18}
          stroke={2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open ? (
        <div className="elva-fade border-t border-[var(--cream-2)]">
          {detailLoading || briefGenerating ? (
            <div className="flex items-center gap-2 px-5 py-5 text-[12.5px] text-[var(--ink-3)]">
              <Icon kind="sparkle" size={15} stroke={1.9} className="text-[var(--ai-3)]" />
              {briefGenerating
                ? 'ELVA is generating an optimization brief for this keyword…'
                : 'Loading optimization brief…'}
            </div>
          ) : row.detail ? (
            <div className="kr-detail-grid grid grid-cols-1 lg:grid-cols-[360px_1fr]">
              <aside className="border-b border-[var(--cream-2)] px-5 py-5 lg:border-b-0 lg:border-r">
                <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--ink-2)]">
                  <Icon kind="search" size={15} stroke={1.9} />
                  What your patient sees on Google
                </h3>
                <div className="mt-4 space-y-3">
                  {row.detail.serp.map((result) => (
                    <KrSerpResult key={result.position} result={result} />
                  ))}
                </div>
              </aside>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--success)]">
                    <Icon kind="sparkle" size={15} stroke={1.9} />
                    ELVA&apos;s Optimization Brief: &ldquo;{row.keyword}&rdquo;
                  </h3>
                  <p className="mt-1.5 text-[12.5px] text-[var(--ink-3)]">{row.detail.brief.comparedTo}</p>
                </div>
                <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-4">
                  <p className={eyebrowClass}>The bottom line</p>
                  <p className="mt-2 text-[12.5px] leading-[1.7] text-[var(--ink-2)]">
                    {row.detail.brief.bottomLine}
                  </p>
                </div>
                <KrAdviceList
                  icon="check-circ"
                  tone="success"
                  title="What You're Doing Right"
                  items={row.detail.brief.doingRight}
                />
                <KrAdviceList
                  icon="alert"
                  tone="gold"
                  title="Critical SEO Gaps"
                  items={row.detail.brief.gaps}
                />
                <KrAdviceList
                  icon="sparkle"
                  tone="success"
                  title="ELVA's Recommended Playbook"
                  items={row.detail.brief.playbook}
                  ordered
                />
                {onRegenerateBrief ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    iconLeft="refresh"
                    onClick={onRegenerateBrief}
                  >
                    Regenerate brief
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-5">
              <div className="flex items-center gap-2 text-[12.5px] text-[var(--ink-3)]">
                <Icon kind="sparkle" size={15} stroke={1.9} className="text-[var(--ai-3)]" />
                No optimization brief is available for this keyword yet.
              </div>
              {onRegenerateBrief ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  iconLeft="sparkle"
                  onClick={onRegenerateBrief}
                >
                  Generate brief
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </KrCard>
  );
}

export interface KeywordRankingsViewProps {
  items?: KrKeyword[];
  stats?: KrStat[];
  measuredFrom?: KrMeasuredFrom;
  summary?: KrSummaryItem[];
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  loading?: boolean;
  category?: string;
  onCategoryChange?: (category: string) => void;
  onPageChange?: (page: number) => void;
  onKeywordExpand?: (id: string | null) => void;
  keywordDetailLoadingId?: string | null;
  keywordBriefGenerating?: boolean;
  onRegenerateBrief?: () => void;
}

export function KeywordRankingsView({
  items: itemsProp,
  stats: statsProp,
  measuredFrom: measuredFromProp,
  summary: summaryProp,
  pagination,
  loading = false,
  category: categoryProp,
  onCategoryChange,
  onPageChange,
  onKeywordExpand,
  keywordDetailLoadingId = null,
  keywordBriefGenerating = false,
  onRegenerateBrief,
}: KeywordRankingsViewProps = {}) {
  const meta = GROWTH_KEYWORDS_META;
  const controlled = itemsProp !== undefined;
  const sourceKeywords = controlled ? itemsProp : KR_KEYWORDS;
  const stats = statsProp ?? KR_STATS;
  const measuredFrom = measuredFromProp ?? KR_MEASURED_FROM;
  const summaryItems = summaryProp ?? KR_SUMMARY;
  const [localFilter, setLocalFilter] = useState<string>('All');
  const filter = categoryProp ?? localFilter;
  const setFilter = onCategoryChange ?? setLocalFilter;
  const [openId, setOpenId] = useState<string>('');

  const rows = useMemo(
    () =>
      controlled
        ? sourceKeywords
        : filter === 'All'
          ? sourceKeywords
          : sourceKeywords.filter((row) => row.category === filter),
    [controlled, filter, sourceKeywords],
  );

  return (
    <div className="growth-keywords-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="search" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--gold)_26%,transparent)] bg-[var(--gold-tint)] px-4 py-3">
        <p className="inline-flex items-center gap-2 text-[13px] text-[var(--gold)]">
          <Icon kind="pin" size={15} stroke={1.9} />
          Search results measured from{' '}
          <span className="font-semibold text-[var(--ink-1)]">{measuredFrom.location}</span> — exactly
          how a patient near your clinic sees Google.
        </p>
        <p className="font-[var(--font-mono)] text-[12px] text-[var(--gold)]">
          Last checked: {measuredFrom.lastChecked}
        </p>
      </div>

      <div className="kr-kpi-grid mb-6 grid gap-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        {stats.length ? (
          stats.map((stat) => (
            <KrStatTile
              key={stat.key}
              label={stat.label}
              value={stat.value}
              sub={stat.sub}
              tone={stat.tone}
              {...(stat.suffix != null ? { suffix: stat.suffix } : {})}
            />
          ))
        ) : (
          <KrCard className="col-span-full px-[18px] py-[15px] text-[13px] text-[var(--ink-3)]">
            Summary tiles load from the keyword rankings database and may show even when the pipeline
            list is empty.
          </KrCard>
        )}
      </div>

      <div className="mb-4">
        <Segmented options={KR_CATEGORIES as unknown as string[]} value={filter} onChange={setFilter} />
      </div>

      <div>
        {rows.length ? (
          rows.map((row) => (
            <KrKeywordRow
              key={row.id}
              row={row}
              open={openId === row.id}
              detailLoading={keywordDetailLoadingId === row.id}
              briefGenerating={keywordBriefGenerating && openId === row.id}
              {...(openId === row.id && onRegenerateBrief
                ? { onRegenerateBrief }
                : {})}
              onToggle={() => {
                const next = openId === row.id ? '' : row.id;
                setOpenId(next);
                onKeywordExpand?.(next || null);
              }}
            />
          ))
        ) : (
          <KrCard className="px-5 py-6 text-[13px] leading-[1.6] text-[var(--ink-3)]">
            {controlled
              ? 'No keyword rankings returned from the pipeline for this filter. Summary tiles above may still reflect tracked keywords in the database.'
              : 'No keywords match this filter.'}
          </KrCard>
        )}
      </div>

      {pagination && onPageChange && pagination.totalPages > 1 ? (
        <div className="mt-6 flex justify-center">
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={onPageChange}
          />
        </div>
      ) : null}

      {summaryItems.length ? (
        <div
          className="mt-6 rounded-[var(--r-xl)] px-6 py-6 text-white"
          style={{ background: 'var(--grad-ink)' }}
        >
          <h2 className="m-0 font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-white">
            ELVA Summary
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-3">
            {summaryItems.map((item) => (
              <article key={item.label}>
                <p className="text-[13px] font-semibold text-white">{item.label}</p>
                <p className="mt-2 text-[12.5px] leading-[1.7] text-white/80">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
